import { Zone } from '@neighbourhood-munchies/shared-types'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { deliveryService } from '../services/foodOrderingApi'
import {
    ZoneError,
    ZoneErrorType,
    getZoneErrorMessage,
    logZoneError,
    retryWithBackoff
} from '../utils/errorHandling'

// ============================================================================
// DELIVERY ZONE CONTEXT TYPES
// ============================================================================

interface DeliveryZoneContextType {
  currentZone: Zone | null
  availableZones: Zone[]
  isLoading: boolean
  error: string | null
  setCurrentZone: (zone: Zone) => void
  refreshZones: () => Promise<void>
  retryLoadZones: () => Promise<void>
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const DeliveryZoneContext = createContext<DeliveryZoneContextType | undefined>(undefined)

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface DeliveryZoneProviderProps {
  children: React.ReactNode
}

export const DeliveryZoneProvider: React.FC<DeliveryZoneProviderProps> = ({ children }) => {
  const [currentZone, setCurrentZoneState] = useState<Zone | null>(null)
  const [availableZones, setAvailableZones] = useState<Zone[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Load zones on mount
  useEffect(() => {
    loadZones()
  }, [])

  // Load current zone from localStorage on mount
  useEffect(() => {
    const savedZone = localStorage.getItem('deliveryZone')
    if (savedZone) {
      try {
        const zone = JSON.parse(savedZone) as Zone
        setCurrentZoneState(zone)
      } catch (error) {
        console.warn('Failed to parse saved delivery zone:', error)
        localStorage.removeItem('deliveryZone')
      }
    }
  }, [])

  const loadZones = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Use retry with backoff for loading zones
      const zones = await retryWithBackoff(
        () => deliveryService.getDeliveryZones(),
        {
          maxRetries: 3,
          initialDelay: 1000,
          onRetry: (attempt, error) => {
            console.warn(`Retrying zone load (attempt ${attempt}/3)...`, error)
            logZoneError(error, {
              operation: 'loadZones',
              additionalInfo: { attempt, retryCount }
            })
          }
        }
      )

      setAvailableZones(zones)
      setRetryCount(0)

      // Set default zone to "San Fernando and Environs" if no zone is selected
      if (!currentZone && zones.length > 0) {
        const defaultZone = zones.find(zone =>
          zone.name.toLowerCase().includes('san fernando')
        ) || zones[0] // Fallback to first zone if San Fernando not found

        setCurrentZoneState(defaultZone)
        localStorage.setItem('deliveryZone', JSON.stringify(defaultZone))
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load delivery zones')
      const zoneError = new ZoneError(
        ZoneErrorType.ZONE_LOAD_FAILED,
        getZoneErrorMessage(error),
        error
      )

      logZoneError(zoneError, {
        operation: 'loadZones',
        additionalInfo: { retryCount }
      })

      setError(zoneError.message)
    } finally {
      setIsLoading(false)
    }
  }

  const retryLoadZones = async () => {
    setRetryCount(prev => prev + 1)
    await loadZones()
  }

  const setCurrentZone = (zone: Zone) => {
    try {
      setCurrentZoneState(zone)
      localStorage.setItem('deliveryZone', JSON.stringify(zone))
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save zone selection')
      const zoneError = new ZoneError(
        ZoneErrorType.ZONE_SELECTION_FAILED,
        getZoneErrorMessage(error),
        error
      )

      logZoneError(zoneError, {
        operation: 'setCurrentZone',
        zoneId: zone.id,
        additionalInfo: { zoneName: zone.name }
      })

      // Still set the zone in state even if localStorage fails
      setCurrentZoneState(zone)
      console.warn('Failed to save zone to localStorage, but zone is set in memory')
    }
  }

  const refreshZones = async () => {
    await loadZones()
  }

  const value: DeliveryZoneContextType = {
    currentZone,
    availableZones,
    isLoading,
    error,
    setCurrentZone,
    refreshZones,
    retryLoadZones,
  }

  return (
    <DeliveryZoneContext.Provider value={value}>
      {children}
    </DeliveryZoneContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

export const useDeliveryZone = (): DeliveryZoneContextType => {
  const context = useContext(DeliveryZoneContext)
  if (context === undefined) {
    throw new Error('useDeliveryZone must be used within a DeliveryZoneProvider')
  }
  return context
}
